import React from "react";
import styled from "styled-components";
import { device, useBreakpoints } from "../../lib/breakpoints";

/**
 * ResponsiveDashboardImage - Displays dashboard screenshots with automatic
 * desktop/mobile switching based on device breakpoint
 *
 * @param {string} desktopSrc - Path to desktop dashboard screenshot
 * @param {string} mobileSrc - Path to mobile dashboard screenshot
 * @param {string} alt - Alt text for the image
 * @param {string} className - Optional CSS class
 * @param {boolean} showPhoneFrame - Whether to show phone frame for mobile images on desktop (default: true)
 * @param {string} screenshotContext - Placeholder title (e.g. "OpsChat"). Default "Dashboard" for "Mobile Dashboard Screenshot".
 */
const ResponsiveDashboardImage = ({
  desktopSrc,
  mobileSrc,
  alt = "LandlordOps Dashboard",
  className,
  showPhoneFrame = true,
  screenshotContext = "Dashboard",
  ...props
}) => {
  const { isMobile } = useBreakpoints();
  const [imageError, setImageError] = React.useState(false);
  const [imageLoaded, setImageLoaded] = React.useState(false);

  // Determine which image to show based on device
  // On mobile: prefer mobileSrc, fallback to desktopSrc
  // On desktop: prefer desktopSrc, fallback to mobileSrc
  const imageSrc = isMobile ? mobileSrc || desktopSrc : desktopSrc || mobileSrc;

  // Check if we're showing a mobile image on desktop (for phone frame)
  // This happens when: on desktop AND showing mobileSrc (either because desktopSrc is missing, or explicitly showing mobile)
  const isShowingMobileImageOnDesktop =
    !isMobile && mobileSrc && imageSrc === mobileSrc;

  const handleImageError = () => {
    setImageError(true);
    setImageLoaded(false);
  };

  // Keep the next screenshot hidden until its bytes are ready to display.
  React.useEffect(() => {
    setImageError(false);
    setImageLoaded(false);
  }, [imageSrc]);

  if (!imageSrc || imageError) {
    return (
      <ImageContainer className={className} $loading={!imageLoaded} {...props}>
        <Placeholder $isMobile={isMobile}>
          <PlaceholderIcon>📊</PlaceholderIcon>
          <PlaceholderText>
            {isMobile
              ? `Mobile ${screenshotContext} Screenshot`
              : `Desktop ${screenshotContext} Screenshot`}
          </PlaceholderText>
          <PlaceholderSubtext>
            {imageError ? "Image failed to load" : "Add screenshot here"}
          </PlaceholderSubtext>
        </Placeholder>
      </ImageContainer>
    );
  }

  // Show mobile image in phone frame when on desktop
  if (isShowingMobileImageOnDesktop && showPhoneFrame) {
    return (
      <ImageContainer className={className} $loading={!imageLoaded} {...props}>
        {!imageLoaded && (
          <LoadingState role="status" aria-live="polite">
            <LoadingSpinner aria-hidden="true" />
            <LoadingText>Loading screenshot</LoadingText>
          </LoadingState>
        )}
        <PhoneFrame>
          <PhoneScreen>
            <PhoneNotch />
            <PhoneImage
              src={imageSrc}
              alt={alt}
              onLoad={() => setImageLoaded(true)}
              onError={handleImageError}
              $loaded={imageLoaded}
            />
            <PhoneHomeIndicator />
          </PhoneScreen>
        </PhoneFrame>
      </ImageContainer>
    );
  }

  // Show mobile image without frame when user is on mobile device
  // Show desktop image or mobile image without frame when on desktop but showPhoneFrame is false
  return (
    <ImageContainer className={className} $loading={!imageLoaded} {...props}>
      {!imageLoaded && (
        <LoadingState role="status" aria-live="polite">
          <LoadingSpinner aria-hidden="true" />
          <LoadingText>Loading screenshot</LoadingText>
        </LoadingState>
      )}
      <DashboardImage
        src={imageSrc}
        alt={alt}
        $isMobile={isMobile || isShowingMobileImageOnDesktop}
        $loaded={imageLoaded}
        onLoad={() => setImageLoaded(true)}
        onError={handleImageError}
      />
    </ImageContainer>
  );
};

export default ResponsiveDashboardImage;

const ImageContainer = styled.div`
  width: 100%;
  min-height: ${(props) =>
    props.$loading ? "clamp(220px, 32vw, 420px)" : "0"};
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
`;

const DashboardImage = styled.img`
  width: 100%;
  height: auto;
  border-radius: 12px;
  box-shadow:
    0 8px 32px rgba(0, 0, 0, 0.12),
    0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(44, 95, 45, 0.1);
  transition:
    opacity 0.4s ease,
    transform 0.4s ease,
    box-shadow 0.3s ease;
  opacity: ${(props) => (props.$loaded ? 1 : 0)};
  transform: ${(props) =>
    props.$loaded ? "translateY(0)" : "translateY(6px)"};
  display: block;
  object-fit: contain;

  ${(props) =>
    props.$isMobile
      ? `
    max-width: 400px;
    border-radius: 24px;
  `
      : `
    max-width: 100%;
  `}

  @media (min-width: 768px) {
    &:hover {
      transform: translateY(-4px);
      box-shadow:
        0 12px 40px rgba(0, 0, 0, 0.15),
        0 4px 12px rgba(0, 0, 0, 0.1);
    }
  }
`;

const Placeholder = styled.div`
  width: 100%;
  min-height: ${(props) => (props.$isMobile ? "600px" : "500px")};
  background: linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%);
  border-radius: 12px;
  border: 2px dashed #ccc;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 3rem;
  text-align: center;

  ${(props) =>
    props.$isMobile
      ? `
    max-width: 400px;
    border-radius: 24px;
    min-height: 700px;
  `
      : `
    max-width: 100%;
  `}
`;

const PlaceholderIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 1rem;
  opacity: 0.5;
`;

const PlaceholderText = styled.div`
  font-size: 1.25rem;
  font-weight: 600;
  color: #666;
  margin-bottom: 0.5rem;
  font-family: var(--font-primary);
`;

const PlaceholderSubtext = styled.div`
  font-size: 0.95rem;
  color: #999;
  font-family: var(--font-secondary);
`;

// Phone Frame Components
const PhoneFrame = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1.5rem;
  perspective: 1000px;
`;

const PhoneScreen = styled.div`
  position: relative;
  width: 100%;
  max-width: 375px;
  background: #1a1a1a;
  border-radius: 2.5rem;
  padding: 0.5rem;
  box-shadow:
    0 20px 60px rgba(0, 0, 0, 0.3),
    0 0 0 0.5rem rgba(0, 0, 0, 0.1),
    inset 0 0 0 0.25rem rgba(255, 255, 255, 0.05);
  transition:
    transform 0.3s ease,
    box-shadow 0.3s ease;

  @media (min-width: 768px) {
    &:hover {
      transform: translateY(-4px);
      box-shadow:
        0 24px 70px rgba(0, 0, 0, 0.35),
        0 0 0 0.5rem rgba(0, 0, 0, 0.1),
        inset 0 0 0 0.25rem rgba(255, 255, 255, 0.05);
    }
  }
`;

const PhoneNotch = styled.div`
  position: absolute;
  top: 0.5rem;
  left: 50%;
  transform: translateX(-50%);
  width: 150px;
  height: 25px;
  background: #1a1a1a;
  border-radius: 0 0 1.25rem 1.25rem;
  z-index: 10;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 6px;
    background: rgba(0, 0, 0, 0.3);
    border-radius: 0 0 3px 3px;
  }
`;

const PhoneImage = styled.img`
  width: 100%;
  height: auto;
  display: block;
  border-radius: 2rem;
  background: #000;
  opacity: ${(props) => (props.$loaded ? 1 : 0)};
  transform: ${(props) =>
    props.$loaded ? "translateY(0)" : "translateY(6px)"};
  transition: opacity 0.4s ease, transform 0.4s ease;
`;

const LoadingState = styled.div`
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.65rem;
  color: var(--color-text-light);
  pointer-events: none;
`;

const LoadingSpinner = styled.span`
  width: 28px;
  height: 28px;
  border: 3px solid rgba(44, 95, 45, 0.18);
  border-top-color: var(--color-primary);
  border-radius: 50%;
  animation: screenshotSpin 0.75s linear infinite;

  @keyframes screenshotSpin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const LoadingText = styled.span`
  font-family: var(--font-secondary);
  font-size: 0.8rem;
  font-weight: 600;
`;

const PhoneHomeIndicator = styled.div`
  position: absolute;
  bottom: 0.75rem;
  left: 50%;
  transform: translateX(-50%);
  width: 134px;
  height: 5px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 2.5px;
  z-index: 10;
`;
